import React, { useEffect, useRef, useState } from 'react';
import { ColorType, CrosshairMode, LineStyle, LineType, createChart, type IChartApi, type UTCTimestamp } from 'lightweight-charts';

async function api(path: string) {
  const r = await fetch('/api/trading' + path);
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || r.statusText);
  return d;
}

const gbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
function fmtR(v: number | null) {
  if (v === null || !Number.isFinite(v)) return '';
  const sign = v > 0 ? '+' : v < 0 ? '−' : '';
  return `${sign}${Math.abs(v).toFixed(2)}R`;
}
function fmtGbp(v: number | null) {
  if (v === null || !Number.isFinite(v)) return '';
  if (v > 0) return `+${gbp.format(v)}`;
  if (v < 0) return `−${gbp.format(Math.abs(v))}`;
  return gbp.format(0);
}

type Marker = { time: number; price: number; r: number | null; kind: 'entry' | 'exit' | 'mfe' | 'mae' };
type Payload = {
  sourceLabel: string;
  error: string | null;
  bars: { time: number; open: number; high: number; low: number; close: number }[];
  entry: number | null;
  initialStop: number | null;
  lockTrigger: number | null;
  takeProfit: number | null;
  stopSteps: { time: number; value: number }[];
  markers: Marker[];
  whatIf: { key: string; label: string; r: number | null; gbp: number | null; why: string; at: string | null }[];
};

export default function TradeChart({ tradeId, theme, direction }: { tradeId: number; theme: 'light' | 'dark'; direction: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<Payload | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let dead = false;
    api(`/trades/${tradeId}/chart`).then((d) => { if (!dead) setData(d); }).catch((e: Error) => { if (!dead) setErr(e.message); });
    return () => { dead = true; };
  }, [tradeId]);

  useEffect(() => {
    if (!ref.current || !data || data.bars.length === 0) return;
    const dark = theme === 'dark';
    const chart: IChartApi = createChart(ref.current, {
      width: ref.current.clientWidth || 320,
      height: 220,
      layout: { background: { type: ColorType.Solid, color: dark ? '#18181b' : '#ffffff' }, textColor: dark ? '#a1a1aa' : '#3f3f46', fontSize: 11 },
      grid: { vertLines: { color: dark ? '#27272a' : '#f4f4f5' }, horzLines: { color: dark ? '#27272a' : '#f4f4f5' } },
      rightPriceScale: { borderVisible: false },
      timeScale: { borderVisible: false, timeVisible: true, secondsVisible: false },
      crosshair: { mode: CrosshairMode.Normal },
      handleScroll: { mouseWheel: false, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
      handleScale: { mouseWheel: false, pinch: true, axisPressedMouseMove: false },
    });
    const candles = chart.addCandlestickSeries({
      upColor: '#10b981', downColor: '#f43f5e', borderVisible: false, wickUpColor: '#10b981', wickDownColor: '#f43f5e',
      priceLineVisible: false,
    });
    candles.setData(data.bars.map((bar) => ({ time: bar.time as UTCTimestamp, open: bar.open, high: bar.high, low: bar.low, close: bar.close })));
    const line = (price: number | null, color: string, title: string, style: LineStyle) => {
      if (price === null || !Number.isFinite(price)) return;
      candles.createPriceLine({ price, color, title, lineStyle: style, lineWidth: 1, axisLabelVisible: true });
    };
    line(data.entry, dark ? '#e4e4e7' : '#3f3f46', 'Entry', LineStyle.Solid);
    line(data.lockTrigger, '#a78bfa', '+1R', LineStyle.Dotted);
    line(data.takeProfit, '#38bdf8', 'Limit', LineStyle.Dashed);
    if (data.stopSteps.length > 1) {
      const stops = chart.addLineSeries({ color: '#f59e0b', lineWidth: 2, lineType: LineType.WithSteps, priceLineVisible: false, lastValueVisible: false });
      stops.setData(data.stopSteps.map((step) => ({ time: step.time as UTCTimestamp, value: step.value })));
    } else if (data.initialStop !== null) {
      line(data.initialStop, '#f59e0b', 'Stop', LineStyle.Dashed);
    }
    const sell = String(direction).toUpperCase() === 'SELL';
    const text = (kind: Marker['kind'], marker: Marker) => {
      const r = fmtR(marker.r);
      if (kind === 'entry') return 'Entry';
      if (kind === 'exit') return `Exit ${r}`.trim();
      if (kind === 'mfe') return `MFE ${r}`.trim();
      return `MAE ${r}`.trim();
    };
    candles.setMarkers(data.markers.map((marker) => ({
      time: marker.time as UTCTimestamp,
      position: marker.kind === 'mae' || (marker.kind === 'entry' && sell) || (marker.kind === 'exit' && !sell) ? 'aboveBar' : 'belowBar',
      color: marker.kind === 'mfe' ? '#34d399' : marker.kind === 'mae' ? '#fb7185' : dark ? '#e4e4e7' : '#3f3f46',
      shape: marker.kind === 'mfe' ? 'arrowUp' : marker.kind === 'mae' ? 'arrowDown' : 'circle',
      text: text(marker.kind, marker),
    })));
    chart.timeScale().fitContent();
    const observer = new ResizeObserver(() => {
      if (ref.current) chart.applyOptions({ width: ref.current.clientWidth });
    });
    observer.observe(ref.current);
    return () => { observer.disconnect(); chart.remove(); };
  }, [data, theme, direction]);

  return (
    <div className="mt-3 border-t border-zinc-200 pt-2 dark:border-zinc-800" aria-label="Trade chart">
      <div className="mb-1 text-xs text-zinc-500">{data?.sourceLabel || (err ? '' : 'Loading chart…')}</div>
      {err && <p className="text-sm text-red-700 dark:text-red-300">{err}</p>}
      {data?.error && <p className="text-sm text-zinc-600 dark:text-zinc-400">{data.error}</p>}
      <div ref={ref} className="h-[220px] w-full" />
      {data && data.bars.length > 0 && data.whatIf.length === 0 && !data.error && (
        <p className="mt-2 text-xs text-zinc-500">Not enough bars after the fill to simulate alternatives yet.</p>
      )}
      {data && data.whatIf.length > 0 && (
        <div className="mt-2">
          <div className="text-xs font-semibold text-zinc-700 dark:text-zinc-300">What if</div>
          <ul className="mt-1 space-y-1">
            {data.whatIf.map((row) => (
              <li key={row.key} className="flex items-baseline justify-between gap-2 text-xs text-zinc-700 dark:text-zinc-300">
                <span className="min-w-0">{row.label}{row.at ? ` · ${row.at}` : ''}</span>
                <span className="shrink-0 tabular-nums font-medium">{fmtR(row.r)}{row.gbp !== null ? ` · ${fmtGbp(row.gbp)}` : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
